import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, LayoutGrid, Users, Palette, FolderKanban, User as UserIcon, Settings } from "lucide-react";
import NotificationsBell from "@/components/NotificationsBell";
import FloatingChatWidget from "@/components/FloatingChatWidget";
import { useT } from "@/lib/i18n";

const roleAccent = { admin: "#1B1E23", pm: "#0E62C4", designer: "#BE2E6C" };

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${
        isActive ? "bg-ink text-white" : "text-ink hover:bg-line/50"
      }`
    }
  >
    <Icon size={16} strokeWidth={1.75} />
    <span>{label}</span>
  </NavLink>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const t = useT();
  const role = user?.rolle;

  return (
    <div className="min-h-screen flex bg-paper text-ink">
      <aside className="w-64 shrink-0 border-r border-line bg-white flex flex-col">
        <div className="p-6 border-b border-line">
          <img src="/logo-brunner.png" alt="Brunner Kundenportal" className="max-w-full h-auto" style={{maxHeight: 36}} />
        </div>

        <nav className="flex-1 p-3 space-y-1">
          <NavItem to="/" icon={LayoutGrid} label={t("nav.dashboard")} testId="nav-dashboard" />
          <NavItem to="/projekte" icon={FolderKanban} label={t("nav.projekte")} testId="nav-projekte" />
          {(role === "admin" || role === "pm") && (
            <>
              <NavItem to="/kunden" icon={Users} label={t("nav.kunden")} testId="nav-kunden" />
              <NavItem to="/designer" icon={Palette} label={t("nav.designer")} testId="nav-designer" />
            </>
          )}
          {role === "admin" && (
            <NavItem to="/verwaltung" icon={Settings} label={t("nav.verwaltung")} testId="nav-verwaltung" />
          )}
          {(role === "admin" || role === "pm") && (
            <NavItem to="/mein-profil" icon={UserIcon} label={t("nav.profil")} testId="nav-mein-profil" />
          )}
          {role === "designer" && (
            <NavItem to="/profil" icon={UserIcon} label={t("nav.profil")} testId="nav-profil" />
          )}
        </nav>

        <div className="p-4 border-t border-line">
          <div className="flex items-center gap-3">
            {user?.foto ? (
              <img src={user.foto} alt="me" className="w-9 h-9 rounded-full object-cover border border-line" data-testid="user-badge" />
            ) : (
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white font-mono text-xs"
                style={{ backgroundColor: roleAccent[role] || "#1B1E23" }}
                data-testid="user-badge"
              >
                {(user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="mono-label mt-0.5">{role}</div>
            </div>
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-btn"
              className="p-2 rounded-sm hover:bg-line/50"
              title={t("nav.logout")}
            >
              <LogOut size={16} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b border-line bg-white flex items-center justify-end px-6 gap-2">
          <NotificationsBell />
        </header>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[1400px] mx-auto p-10">
            <Outlet />
          </div>
        </div>
      </main>
      <FloatingChatWidget />
    </div>
  );
}
